import { shipmentReference } from "@/lib/shipment-reference";
import { useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  useUnroutedCarrierDropoffs,
  useBulkAssignLocation,
  type CarrierTaskGroup,
} from "@/api/tasks";
import { useGetAdminDropoffLocations } from "@/api/admin-carrier-dropoff-locations";
import { useCarriers } from "@/api/carriers";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { taskRefs } from "./task-labels";

/**
 * Carrier-dropoff exceptions inbox for the dispatch Tasks tab.
 * CARRIER_DROPOFF tasks whose drop-off lookup failed at order time, grouped by
 * label carrier → pick a saved location → bulk assign.
 */
export function ExceptionsInbox() {
  const { data, refetch, isLoading, error } = useUnroutedCarrierDropoffs();

  // Group by the final/label dropoff carrier and show them all — these are the
  // owned carrier's shipments needing a hand-off destination, so we don't
  // filter by the dispatch carrier scope (that would hide the provider groups).
  const groups = data?.data ?? [];
  const total = groups.reduce((n, g) => n + g.tasks.length, 0);

  return (
    <div className="p-3">
      <div className="mb-1 flex items-center gap-2 px-0.5">
        {total === 0 ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
        ) : (
          <AlertCircle className="h-3.5 w-3.5 text-orange-500" />
        )}
        <h3 className="text-[11px] font-bold uppercase tracking-wide text-zinc-700">
          Carrier-dropoff exceptions
        </h3>
        {total > 0 && (
          <span className="ml-auto text-[11px] font-semibold tabular-nums text-orange-700">
            {total} need assignment
          </span>
        )}
      </div>
      <p className="mb-3 px-0.5 text-[11.5px] leading-snug text-zinc-500">
        Tasks auto-route from the carrier&apos;s saved default. Anything here
        needs a destination picked by hand.
      </p>

      {isLoading && (
        <div className="flex items-center justify-center py-10 text-[12.5px] text-zinc-500">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading exceptions…
        </div>
      )}
      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-[12.5px] text-red-600">
          {error instanceof Error ? error.message : "Failed to load"}
        </div>
      )}
      {!isLoading && !error && groups.length === 0 && (
        <div className="py-10 text-center">
          <div className="text-[13px] font-medium text-zinc-700">
            No carrier-dropoff exceptions
          </div>
          <div className="mt-1 text-[11.5px] text-zinc-500">
            Everything routed to a drop-off automatically.
          </div>
        </div>
      )}

      <div className="space-y-2">
        {groups.map((g) => (
          <ExceptionGroup
            key={g.carrierCode}
            group={g}
            onAssignSuccess={() => void refetch()}
          />
        ))}
      </div>
    </div>
  );
}

function ExceptionGroup({
  group,
  onAssignSuccess,
}: {
  group: CarrierTaskGroup;
  onAssignSuccess: () => void;
}) {
  const [open, setOpen] = useState(true);
  const [sel, setSel] = useState<string[]>([]);
  const [locationId, setLocationId] = useState("");

  const carriers = useCarriers();
  const labelCarrierId = carriers.data?.data.find(
    (c) => c.code === group.carrierCode
  )?.id;
  const { data: locations, isLoading: locLoading } =
    useGetAdminDropoffLocations(labelCarrierId);
  const activeLocations = (locations ?? []).filter((l) => l.active);
  const bulkAssign = useBulkAssignLocation();

  const toggle = (id: string) =>
    setSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const assign = () => {
    if (sel.length === 0) return toast.error("Select at least one task");
    if (!locationId) return toast.error("Pick a drop-off location");
    bulkAssign.mutate(
      { taskIds: sel, carrierDropoffLocationId: locationId },
      {
        onSuccess: () => {
          toast.success("Drop-off assigned", {
            description: `${sel.length} task(s) routed.`,
          });
          setSel([]);
          setLocationId("");
          onAssignSuccess();
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } } };
          toast.error("Failed to assign", {
            description: e.response?.data?.error ?? "Unknown error",
          });
        },
      }
    );
  };

  return (
    <div
      className="overflow-hidden rounded-xl"
      style={{ background: "#fff7ed", border: "1px solid #fed7aa" }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
      >
        <AlertCircle className="h-4 w-4 text-orange-500" />
        <span className="text-[13px] font-bold text-zinc-900">
          {group.carrierName}
        </span>
        <span className="text-[11px] font-semibold text-orange-700">
          ({group.tasks.length} unrouted)
        </span>
        <div className="flex-1" />
        {open ? (
          <ChevronDown className="h-4 w-4 text-orange-700" />
        ) : (
          <ChevronRight className="h-4 w-4 text-orange-700" />
        )}
      </button>

      {open && (
        <div className="space-y-2 bg-white p-3 pt-2">
          <div className="flex gap-2">
            <button
              onClick={() => setSel(group.tasks.map((t) => t.id))}
              className="text-[11.5px] font-semibold text-indigo-600 hover:underline"
            >
              All
            </button>
            <span className="text-zinc-300">·</span>
            <button
              onClick={() => setSel([])}
              disabled={sel.length === 0}
              className="text-[11.5px] font-semibold text-zinc-500 hover:underline disabled:opacity-40"
            >
              Deselect all
            </button>
          </div>

          <div className="-mx-1 max-h-[200px] space-y-0.5 overflow-y-auto">
            {group.tasks.map((task) => {
              const refs = taskRefs(task);
              return (
                <label
                  key={task.id}
                  className="flex cursor-pointer items-start gap-2 rounded p-2 hover:bg-zinc-50"
                >
                  <Checkbox
                    checked={sel.includes(task.id)}
                    onCheckedChange={() => toggle(task.id)}
                    className="mt-0.5"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[12px] font-medium text-zinc-900">
                      {shipmentReference(refs.shipmentNumber)}
                      <span className="text-zinc-500">
                        {" "}
                        · TSK-{task.taskNumber}
                      </span>
                      {refs.orderNumber != null && (
                        <span className="text-zinc-400">
                          {" "}
                          · ORD-{refs.orderNumber}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-amber-700">
                      ⚠ No drop-off assigned
                    </div>
                  </div>
                </label>
              );
            })}
          </div>

          <div className="flex gap-2 pt-1">
            <Select
              value={locationId}
              onValueChange={setLocationId}
              disabled={locLoading || activeLocations.length === 0}
            >
              <SelectTrigger className="h-9 flex-1 text-[12px]">
                <SelectValue
                  placeholder={
                    locLoading
                      ? "Loading…"
                      : activeLocations.length === 0
                        ? `No ${group.carrierName} locations`
                        : "Pick drop-off location…"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {activeLocations.map((loc) => (
                  <SelectItem
                    key={loc.id}
                    value={loc.id}
                    className="text-[12px]"
                  >
                    {loc.name} — {loc.address.line1}, {loc.address.city}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={assign}
              disabled={!sel.length || !locationId || bulkAssign.isPending}
              className="h-9 bg-indigo-600 text-[12px] hover:bg-indigo-700"
            >
              {bulkAssign.isPending ? "Assigning…" : "Assign"}
            </Button>
          </div>

          <a
            href="/add-on-services/grants"
            className="block pt-1 text-[11px] text-indigo-600 hover:underline"
          >
            Set a default for this carrier so future tasks auto-route →
          </a>
        </div>
      )}
    </div>
  );
}
